import { useEffect, useRef, useState } from "react";
import { api, fmtNumber } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { FlaskConical, RotateCcw, Save, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const FACTOR_ORDER = ["condition", "risk", "remaining_life", "utilisation", "environment", "cost"];
const HZ_BADGE = {
  "Immediate Attention": "bg-rose-100 text-rose-700",
  "12 Month Plan": "bg-amber-100 text-amber-700",
  "24 Month Plan": "bg-blue-100 text-blue-700",
  Monitor: "bg-slate-100 text-slate-600",
};

export function PortalMethodology({ qp, onApplied }) {
  const [cfg, setCfg] = useState(null);
  const [weights, setWeights] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);
  const seqRef = useRef(0);

  const load = async () => {
    const r = await api.get(`/client-portal/methodology${qp}`);
    setCfg(r.data);
    setWeights(r.data.weights);
  };
  useEffect(() => { load().catch(() => toast.error("Failed to load methodology")); /* eslint-disable-next-line */ }, [qp]);

  const runPreview = (w) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const seq = ++seqRef.current;
      setPreviewing(true);
      try {
        const r = await api.post(`/client-portal/methodology/preview${qp}`, { weights: w }, { timeout: 60000 });
        if (seq === seqRef.current) setPreview(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Preview failed");
      } finally { if (seq === seqRef.current) setPreviewing(false); }
    }, 500);
  };

  const setW = (key, val) => {
    const w = { ...weights, [key]: val };
    setWeights(w);
    runPreview(w);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/client-portal/methodology${qp}`, { weights }, { timeout: 60000 });
      toast.success("Custom weighting applied to your portfolio — other sites are unaffected");
      await load();
      setPreview(null);
      onApplied?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to apply weighting");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await api.post(`/client-portal/methodology/reset${qp}`);
      toast.success("Reverted to GBE's standard weighting");
      await load();
      setPreview(null);
      onApplied?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reset");
    } finally { setSaving(false); }
  };

  if (!cfg || !weights) return <div className="h-64 bg-white border border-slate-200 rounded-lg shimmer" />;
  const totalWeight = FACTOR_ORDER.reduce((s, k) => s + (weights[k] || 0), 0);

  return (
    <div className="space-y-4" data-testid="portal-methodology-tab">
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-center gap-3">
          <FlaskConical className="w-5 h-5 text-gbe" />
          <h2 className="font-display text-lg font-semibold text-slate-900">How your assets are prioritised</h2>
          {cfg.active ? (
            <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100" data-testid="portal-methodology-status">Your custom weighting active</Badge>
          ) : (
            <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100" data-testid="portal-methodology-status">GBE standard weighting</Badge>
          )}
          <span className="ml-auto text-xs text-slate-500">{fmtNumber(cfg.asset_count)} assets in your portfolio</span>
        </div>
        <p className="text-sm text-slate-600 mt-3 max-w-3xl">{cfg.formula}</p>
        <div className="mt-3 inline-flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="portal-methodology-scope-note">
          <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Adjusting these sliders re-prioritises <strong>your assets only</strong>. GBE's global rankings and every other client's data are never affected, and the number of assets in each planning horizon stays the same — your weighting changes which assets fill them.</span>
        </div>
        {cfg.updated_by && cfg.active && (
          <p className="text-[11px] text-slate-400 mt-2">Last changed by {cfg.updated_by} · {(cfg.updated_at || "").slice(0, 16).replace("T", " ")}</p>
        )}
      </div>

      <div className="grid md:grid-cols-5 gap-3">
        {cfg.pipeline.map((p) => (
          <div key={p.step} className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-[10px] uppercase tracking-wider text-gbe font-semibold">{p.step}</div>
            <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{p.detail}</p>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-600 font-medium">Your factor weights</span>
          <span className="text-[11px] text-slate-400">total {Math.round(totalWeight)}</span>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-8" onClick={reset} disabled={saving} data-testid="portal-methodology-reset-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> GBE standard
            </Button>
            <Button size="sm" className="h-8 bg-gbe hover:bg-gbe-hover text-white" onClick={save} disabled={saving} data-testid="portal-methodology-save-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              Apply to my portfolio
            </Button>
          </div>
        </div>
        <div className="divide-y divide-slate-100">
          {cfg.factor_docs.map((f) => (
            <div key={f.key} className="px-5 py-3 grid md:grid-cols-[170px_1fr_240px_70px] gap-3 items-center">
              <div>
                <div className="text-sm font-medium text-slate-900">{f.label}</div>
                <div className="text-[10px] text-slate-400">default {cfg.default_weights[f.key]}</div>
              </div>
              <div className="text-xs text-slate-500">{f.source}</div>
              <Slider value={[weights[f.key] ?? 0]} min={0} max={50} step={1}
                      onValueChange={([v]) => setW(f.key, v)}
                      data-testid={`portal-methodology-slider-${f.key}`} />
              <div className="text-right">
                <span className="text-sm font-semibold text-slate-900 tabular-nums">{weights[f.key] ?? 0}</span>
                <span className="text-[10px] text-slate-400 ml-1">
                  {totalWeight > 0 ? `${Math.round(((weights[f.key] || 0) / totalWeight) * 100)}%` : "0%"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-600 font-medium">Live preview — your top 20 assets under this weighting</span>
          {previewing && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
          {preview && (
            <span className="ml-auto text-[11px] text-slate-500">
              Beyond Life {preview.status_counts["Immediate Attention"]} · 12M {preview.status_counts["12 Month Plan"]} ·
              24M {preview.status_counts["24 Month Plan"]} · Monitor {preview.status_counts["Monitor"]}
            </span>
          )}
        </div>
        {!preview ? (
          <div className="p-6 text-sm text-slate-500 text-center">Move any slider to preview your re-prioritised portfolio — nothing is saved until you click "Apply to my portfolio".</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="portal-methodology-preview-table">
              <thead>
                <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left font-medium px-3 py-2">#</th>
                  <th className="text-left font-medium px-3 py-2">Asset</th>
                  <th className="text-left font-medium px-3 py-2">Site</th>
                  <th className="text-right font-medium px-3 py-2">New score</th>
                  <th className="text-left font-medium px-3 py-2">New horizon</th>
                  <th className="text-left font-medium px-3 py-2">Was</th>
                </tr>
              </thead>
              <tbody>
                {preview.top.map((a) => (
                  <tr key={a.asset_id} className="border-b border-slate-100">
                    <td className="px-3 py-2 font-semibold tabular-nums">{a.new_rank}</td>
                    <td className="px-3 py-2 text-slate-800 max-w-[280px] truncate">{a.asset_name}</td>
                    <td className="px-3 py-2 text-slate-600 max-w-[180px] truncate">{a.site_name}</td>
                    <td className="px-3 py-2 text-right font-medium tabular-nums">{a.score}</td>
                    <td className="px-3 py-2"><Badge className={`text-[10px] hover:bg-inherit ${HZ_BADGE[a.horizon]}`}>{a.horizon === "Immediate Attention" ? "Beyond Design Life" : a.horizon}</Badge></td>
                    <td className="px-3 py-2"><Badge className={`text-[10px] hover:bg-inherit opacity-60 ${HZ_BADGE[a.old_horizon] || "bg-slate-100 text-slate-600"}`}>{a.old_horizon || "—"}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
